import type { AgilePlanApproval, AgilePlanRow, AnyWriteApproval, ClientStatus, ClientStatusApproval, ClientStatusKind, NewClientApproval, WriteApproval } from './types';

export function formatApprovalValue(value: unknown): string {
  if (value == null || value === '') return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export function writeApprovalAfter(card: WriteApproval): string {
  return typeof card.after === 'string' ? card.after : 'not provided';
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const SERVICES = new Set(['seo', 'ads', 'both']);
const STATUSES = new Set<string>(['active', 'paused', 'offboarded']);
const STATUS_TOOLS: Record<ClientStatusKind, string> = {
  offboard_client: 'core__core_offboard-client',
  reactivate_client: 'core__core_reactivate-client',
};

/** The New client card from the backend payload, or undefined when it is not one a person could
 *  approve (no request id, no client name, an unknown service, a preview without both flags):
 *  no card is better than an Approve button that could not settle. */
function newClientFromPayload(raw: Record<string, unknown>): NewClientApproval | undefined {
  const requestId = raw.request_id;
  const client = raw.client;
  const preview = raw.preview;
  if (typeof requestId !== 'string' || !requestId || !isRecord(client) || !isRecord(preview)) return undefined;
  const name = typeof client.name === 'string' ? client.name.trim() : '';
  const services = client.services;
  const willCreate = preview.will_create;
  if (!name || typeof services !== 'string' || !SERVICES.has(services)) return undefined;
  if (!isRecord(willCreate) || typeof willCreate.tenant !== 'boolean' || typeof willCreate.workspace !== 'boolean') {
    return undefined;
  }
  const website = typeof client.website === 'string' ? client.website : '';
  const members = Array.isArray(raw.members)
    ? raw.members.flatMap((item) =>
        isRecord(item) && typeof item.name === 'string' && item.name.trim()
          ? [{ id: typeof item.id === 'string' ? item.id : '', name: item.name }]
          : [],
      )
    : [];
  const unmatched = typeof raw.unmatched_member_count === 'number' && raw.unmatched_member_count > 0
    ? Math.floor(raw.unmatched_member_count)
    : 0;
  const notice = typeof raw.notice === 'string' && raw.notice.trim() ? raw.notice : null;
  return {
    kind: 'new_client',
    requestId,
    toolName: typeof raw.tool_name === 'string' ? raw.tool_name : 'core__core_create-client',
    client: {
      name: client.name as string,
      website,
      services: services as NewClientApproval['client']['services'],
      approvalRequired: client.approval_required === true,
    },
    members,
    unmatchedMemberCount: unmatched,
    preview: {
      website: typeof preview.website === 'string' ? preview.website : website,
      willCreate: { tenant: willCreate.tenant, workspace: willCreate.workspace },
      tenantExists: typeof preview.tenant_exists === 'boolean' ? preview.tenant_exists : !willCreate.tenant,
      workspaceExists: typeof preview.workspace_exists === 'boolean' ? preview.workspace_exists : !willCreate.workspace,
    },
    declineOnly: raw.decline_only !== false || notice !== null,
    notice,
    dryRun: raw.dry_run === true,
  };
}

/** The offboard / reactivate card from the backend payload, or undefined when nothing could be approved
 *  (no request id, no client website, a status that does not exist). Decline-only fails closed. */
function clientStatusFromPayload(raw: Record<string, unknown>, kind: ClientStatusKind): ClientStatusApproval | undefined {
  const requestId = raw.request_id;
  const client = raw.client;
  if (typeof requestId !== 'string' || !requestId || !isRecord(client)) return undefined;
  const website = typeof client.website === 'string' ? client.website.trim() : '';
  if (!website) return undefined;
  const status = (value: unknown): ClientStatus | null | undefined =>
    value == null ? null : typeof value === 'string' && STATUSES.has(value) ? (value as ClientStatus) : undefined;
  const before = status(raw.status_before);
  const after = status(raw.status_after);
  if (before === undefined || after === undefined) return undefined;
  const notice = typeof raw.notice === 'string' && raw.notice.trim() ? raw.notice : null;
  return {
    kind,
    requestId,
    toolName: typeof raw.tool_name === 'string' && raw.tool_name ? raw.tool_name : STATUS_TOOLS[kind],
    client: { name: typeof client.name === 'string' && client.name.trim() ? client.name : null, website },
    statusBefore: before,
    statusAfter: after,
    declineOnly: raw.decline_only !== false || notice !== null,
    notice,
    dryRun: raw.dry_run === true,
  };
}

function agilePlanRowFromPayload(raw: unknown): AgilePlanRow | undefined {
  if (!isRecord(raw) || typeof raw.index !== 'number' || typeof raw.kind !== 'string' || !raw.kind) return undefined;
  const labels = isRecord(raw.labels) ? raw.labels : {};
  const text = (value: unknown): string | null => (typeof value === 'string' && value ? value : null);
  return {
    index: raw.index,
    kind: raw.kind,
    valid: raw.valid === true,
    errors: Array.isArray(raw.errors) ? raw.errors.filter((e): e is string => typeof e === 'string') : [],
    noop: raw.noop === true,
    target: raw.target ?? null,
    labels: { client: text(labels.client), task_type: text(labels.task_type), billing_period: text(labels.billing_period) },
    changes: Array.isArray(raw.changes)
      ? raw.changes.flatMap((c) => (isRecord(c) && typeof c.field === 'string' ? [{ field: c.field, from: c.from ?? null, to: c.to ?? null }] : []))
      : [],
    before: raw.before ?? null,
    after: raw.after ?? null,
    diff: isRecord(raw.diff)
      ? { removed: typeof raw.diff.removed === 'string' ? raw.diff.removed : '', added: typeof raw.diff.added === 'string' ? raw.diff.added : '' }
      : null,
    teamworkIdentity: text(raw.teamwork_identity),
    teamworkIdentityLabel: text(raw.teamwork_identity_label),
  };
}

/** The Agile Plan card from the backend payload, or undefined when nothing could be approved (no request id, no
 *  rows, a row without an index or kind). Decline-only fails closed: it is also true when no row is both valid and
 *  a real change. */
function agilePlanFromPayload(raw: Record<string, unknown>): AgilePlanApproval | undefined {
  const requestId = raw.request_id;
  if (typeof requestId !== 'string' || !requestId || !Array.isArray(raw.rows) || raw.rows.length === 0) return undefined;
  const rows = raw.rows.map(agilePlanRowFromPayload);
  if (rows.some((row) => row === undefined)) return undefined;
  const parsed = rows as AgilePlanRow[];
  const notice = typeof raw.notice === 'string' && raw.notice.trim() ? raw.notice : null;
  const summary = isRecord(raw.summary) ? raw.summary : {};
  const count = (value: unknown, fallback: number) => (typeof value === 'number' ? value : fallback);
  const applicable = parsed.filter((row) => row.valid && !row.noop).length;
  return {
    kind: 'agile_plan_changes',
    requestId,
    toolName: typeof raw.tool_name === 'string' ? raw.tool_name : 'core__core_agile-plan-propose-changes',
    client: { website: isRecord(raw.client) && typeof raw.client.website === 'string' ? raw.client.website : '' },
    rows: parsed,
    summary: {
      total: count(summary.total, parsed.length),
      toApply: count(summary.to_apply, applicable),
      invalid: count(summary.invalid, parsed.filter((row) => !row.valid).length),
      noop: count(summary.noop, parsed.filter((row) => row.valid && row.noop).length),
    },
    dryRun: raw.dry_run === true,
    createdAt: typeof raw.created_at === 'string' ? raw.created_at : '',
    expiresAt: typeof raw.expires_at === 'string' ? raw.expires_at : '',
    declineOnly: raw.decline_only !== false || notice !== null || applicable === 0,
    notice,
  };
}

export function writeApprovalFromPayload(
  raw: Record<string, unknown> | null | undefined,
): AnyWriteApproval | undefined {
  if (raw && raw.kind === 'new_client') return newClientFromPayload(raw);
  if (raw && raw.kind === 'agile_plan_changes') return agilePlanFromPayload(raw);
  if (raw && (raw.kind === 'offboard_client' || raw.kind === 'reactivate_client')) {
    return clientStatusFromPayload(raw, raw.kind);
  }
  if (!raw || typeof raw.request_id !== 'string' || typeof raw.tool_name !== 'string') {
    return undefined;
  }
  return {
    requestId: raw.request_id,
    toolName: raw.tool_name,
    postId: (raw.post_id as string | number | null | undefined) ?? null,
    field: typeof raw.field === 'string' && raw.field ? raw.field : 'content',
    before: raw.before,
    after: raw.after ?? null,
  };
}

interface WriteApprovalCardProps {
  card: WriteApproval;
  busy?: boolean;
  onApprove: () => void;
  onDecline: () => void;
}

export function WriteApprovalCard({ card, busy = false, onApprove, onDecline }: WriteApprovalCardProps) {
  return (
    <div
      role="group"
      aria-label="Approve or decline this write"
      className="mt-2 rounded-lg border border-warm-border bg-paper px-3 py-2.5"
    >
      <dl className="grid gap-1.5 font-mono text-[11px] text-ink-2">
        <div>
          <dt className="text-[10px] uppercase tracking-wider text-ink-3">Tool</dt>
          <dd className="text-ink">{card.toolName}</dd>
        </div>
        <div>
          <dt className="text-[10px] uppercase tracking-wider text-ink-3">Post</dt>
          <dd className="text-ink">{card.postId == null || card.postId === '' ? 'new draft' : String(card.postId)}</dd>
        </div>
        <div>
          <dt className="text-[10px] uppercase tracking-wider text-ink-3">Field</dt>
          <dd className="text-ink">{card.field}</dd>
        </div>
        <div>
          <dt className="text-[10px] uppercase tracking-wider text-ink-3">Before</dt>
          <dd className="whitespace-pre-wrap text-ink">{formatApprovalValue(card.before)}</dd>
        </div>
        <div>
          <dt className="text-[10px] uppercase tracking-wider text-ink-3">After</dt>
          <dd className="whitespace-pre-wrap text-ink">{formatApprovalValue(writeApprovalAfter(card))}</dd>
        </div>
      </dl>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={onApprove}
          className="rounded-md bg-coral px-2.5 py-1 text-[11px] font-medium text-white hover:bg-coral-hover disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
        >
          Approve
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onDecline}
          className="rounded-md border border-warm-border bg-paper-2 px-2.5 py-1 text-[11px] font-medium text-ink-2 hover:border-warm-border-strong disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
        >
          Decline
        </button>
      </div>
    </div>
  );
}
